import { type WidgetType } from '@fruitful/types';

import { DATA_UI_CHUNK_TYPE } from '../constants/streamDataChunkTypes';
import type { MastraSSEData, MastraUIStreamData } from '../types/ChatTypes';
import { isRecord } from './parsingHelpers';

/** Extract `{ widget, props }` from an object, unwrapping one nested `data-ui` layer if present. */
const parseWidgetPayload = (
    value: unknown,
): { widget: WidgetType; props: Record<string, unknown> | null } | null => {
    if (!isRecord(value)) {
        return null;
    }

    if (typeof value.widget === 'string') {
        return {
            widget: value.widget as WidgetType,
            props: isRecord(value.props) ? value.props : null,
        };
    }

    if (value.type === DATA_UI_CHUNK_TYPE && isRecord(value.data)) {
        return parseWidgetPayload(value.data);
    }

    return null;
};

/**
 * Normalize Mastra SSE chunks into widget render props.
 *
 * Tools emit `writer.custom({ type: 'data-ui', data: { widget, props } })`, but the
 * stream may wrap that payload under different top-level fields (`data`, `payload`,
 * or at the root) depending on platform/transport. Each branch below picks a likely
 * envelope; all funnel into `parseWidgetPayload`, which finds the same `{ widget, props }`
 * shape once unwrapped.
 */
export const parseUIChunkFromSSE = (
    data: MastraSSEData,
): { widget: WidgetType; props: Record<string, unknown> | null } | null => {
    if (data.type === DATA_UI_CHUNK_TYPE) {
        const direct = parseWidgetPayload(data.data ?? data);
        if (direct != null) {
            return direct;
        }
    }

    if (data.type === 'custom' || data.type === 'data') {
        const fromCustom = parseWidgetPayload(data.data);
        if (fromCustom != null) {
            return fromCustom;
        }
    }

    const fromPayload = parseWidgetPayload(data.payload);
    if (fromPayload != null) {
        return fromPayload;
    }

    return parseWidgetPayload(data as unknown as MastraUIStreamData);
};
